import * as React from 'react';
import { AlignType, BorderType, BrowserType } from '../../wui-core/src/types/iCore';
import {
    AlignContextProps,
    BorderContextProps,
    BrowserContextProps,
    DisabledContextProps,
    LayoutContextProps,
    ReadOnlyContextProps,
    RTLContextProps,
    SizeContextProps,
} from './iProvider';

/**
 * 创建简单的 Context 和 Provider 的工厂函数
 */
function createSimpleContext<T>(
    defaultValue: T,
    contextName?: string
): {
    Context: React.Context<T>;
    Provider: React.FC<{
        value?: T;
        children?: React.ReactNode;
    }>;
} {
    const Context = React.createContext<T>(defaultValue);

    if (contextName && process.env.NODE_ENV !== 'production') {
        Context.displayName = contextName;
    }

    const Provider: React.FC<{
        value?: T;
        children?: React.ReactNode;
    }> = ({ children, value }) => (
        <Context.Consumer>
            {(originValue) => (
                <Context.Provider value={(value as any) || originValue}>
                    {children}
                </Context.Provider>
            )}
        </Context.Consumer>
    );

    // eslint-disable-next-line react/prop-types
    Provider.displayName = contextName ? `${contextName}Provider` : 'SimpleContextProvider';

    return {
        Context,
        Provider,
    };
}

// SizeContext
export const sizeMap = {
    xs: 'xs',
    sm: 'sm',
    md: 'md',
    nm: 'nm',
    xg: 'xg',
    lg: 'lg',
    small: 'sm',
    middle: 'md',
    large: 'lg',
};

const { Context: SizeContext, Provider: SizeBaseProvider } = createSimpleContext<any>(
    undefined,
    'SizeContext'
);

// eslint-disable-next-line react/prop-types
export const SizeContextProvider: React.FC<SizeContextProps> = ({ children, size }) => (
    <SizeBaseProvider value={size}>{children}</SizeBaseProvider>
);

// AlignContext
const { Context: AlignContext, Provider: AlignBaseProvider } = createSimpleContext<AlignType>(
    'left' as AlignType,
    'AlignContext'
);

// eslint-disable-next-line react/prop-types
export const AlignContextProvider: React.FC<AlignContextProps> = ({ children, align }) => (
    <AlignBaseProvider value={align}>{children}</AlignBaseProvider>
);

// BorderContext
const { Context: BorderContext, Provider: BorderBaseProvider } = createSimpleContext<BorderType>(
    true as BorderType,
    'BorderContext'
);

// eslint-disable-next-line react/prop-types
export const BorderContextProvider: React.FC<BorderContextProps> = ({ children, bordered }) => (
    <BorderBaseProvider value={bordered}>{children}</BorderBaseProvider>
);

// BrowserContext
const { Context: BrowserContext, Provider: BrowserBaseProvider } = createSimpleContext<BrowserType>(
    true as BrowserType,
    'BrowserContext'
);

// eslint-disable-next-line react/prop-types
export const BrowserContextProvider: React.FC<BrowserContextProps> = ({ children, browser }) => (
    <BrowserBaseProvider value={browser}>{children}</BrowserBaseProvider>
);

// DisabledContext
const { Context: DisabledContext, Provider: DisabledBaseProvider } = createSimpleContext<boolean>(
    false,
    'DisabledContext'
);

// eslint-disable-next-line react/prop-types
export const DisabledContextProvider: React.FC<DisabledContextProps> = ({ children, disabled }) => (
    <DisabledBaseProvider value={disabled}>{children}</DisabledBaseProvider>
);

// ReadOnlyContext
const { Context: ReadOnlyContext, Provider: ReadOnlyBaseProvider } = createSimpleContext<boolean>(
    false,
    'ReadOnlyContext'
);

// eslint-disable-next-line react/prop-types
export const ReadOnlyContextProvider: React.FC<ReadOnlyContextProps> = ({ children, readOnly }) => (
    <ReadOnlyBaseProvider value={readOnly}>{children}</ReadOnlyBaseProvider>
);

// LayoutContext
const { Context: LayoutContext, Provider: LayoutBaseProvider } = createSimpleContext<'vertical' | 'horizontal' | 'inline'>(
    'vertical',
    'LayoutContext'
);

// eslint-disable-next-line react/prop-types
export const LayoutContextProvider: React.FC<LayoutContextProps> = ({ children, layout }) => (
    <LayoutBaseProvider value={layout}>{children}</LayoutBaseProvider>
);

// RTLContext
const { Context: RTLContext, Provider: RTLBaseProvider } = createSimpleContext<'ltr' | 'rtl' | undefined>(
    undefined,
    'RTLContext'
);

// eslint-disable-next-line react/prop-types
export const RTLContextProvider: React.FC<RTLContextProps> = ({ children, dir }) => (
    <RTLBaseProvider value={dir}>{children}</RTLBaseProvider>
);

export const useRtl = () => React.useContext(RTLContext);

// Named exports
export { AlignContext, BorderContext, BrowserContext, DisabledContext, LayoutContext, ReadOnlyContext, RTLContext, SizeContext };
